/**
 * ProgramCard Component
 * Individual program card with all status indicators and actions
 */

import React, { useState, useEffect, useMemo } from 'react';
import { FaEllipsisV, FaEdit, FaCog, FaTrash, FaCalendarAlt } from 'react-icons/fa';
import { getProgramMembers } from '../../../../api/programs';
import { useUser } from '../../../../context/UserContext';
import { useFollowingStatus } from '../../../../hooks/useFollowingStatus';
import CapacityIndicator from '../../../../components/Program/CapacityIndicator';
import MembershipStatusBadge from '../../../../components/Program/MembershipStatusBadge';
import FollowButton from '../../../../components/Program/FollowButton';
import RoleBadge from '../../../../components/Program/RoleBadge';
import SmartActionButton from '../SmartActionButton';
import { getSportIcon, formatProgramPrice, getRegistrationDates } from '../../utils/programHelpers';
import styles from './ProgramCard.module.css';

const ProgramCard = ({
  program,
  clubId,
  onClick,
  canManage = false,
  isClubManager = false,
  userClubRole = null,
  onEdit,
  onDelete,
  onManage,
  onJoinClubRequired,
  onRegistrationComplete
}) => {
  const { userProfile } = useUser();
  const followingStatus = useFollowingStatus(program?._id);
  
  // State
  const [myMembershipStatus, setMyMembershipStatus] = useState(null);
  const [myCanApply, setMyCanApply] = useState(false);
  const [enrolledCount, setEnrolledCount] = useState(0);
  const [membershipLoading, setMembershipLoading] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);

  // Fetch membership status
  useEffect(() => {
    let mounted = true;

    const fetchMembershipData = async () => {
      if (!program?._id || !userProfile?._id) {
        setMembershipLoading(false);
        return;
      }

      setMembershipLoading(true);

      try {
        // Fetch enrolled count
        const enrolled = await getProgramMembers(program._id, { status: 'Enrolled' });
        if (mounted) {
          setEnrolledCount(Array.isArray(enrolled) ? enrolled.length : 0);
        }
      } catch (err) {
        console.error('Error fetching enrolled members:', err);
        if (mounted) setEnrolledCount(0);
      }

      try {
        // Fetch user's membership status
        const all = await getProgramMembers(program._id);
        const mine = Array.isArray(all) 
          ? all.find(m => (m.userId || m.user?._id) === userProfile._id)
          : null;
        if (mounted) {
          setMyMembershipStatus(mine?.status || null);
          setMyCanApply(mine?.canApplyDespiteRestrictions || false);
        }
      } catch (err) {
        console.error('Error fetching membership status:', err);
        if (mounted) { setMyMembershipStatus(null); setMyCanApply(false); }
      } finally {
        if (mounted) setMembershipLoading(false);
      }
    };

    fetchMembershipData();

    return () => { mounted = false; };
  }, [program?._id, userProfile?._id]);

  // Refresh membership state (callback for action button)
  const refreshMembershipState = async () => {
    if (!program?._id) return;

    try {
      const enrolled = await getProgramMembers(program._id, { status: 'Enrolled' });
      setEnrolledCount(Array.isArray(enrolled) ? enrolled.length : 0);

      const all = await getProgramMembers(program._id);
      const mine = Array.isArray(all) 
        ? all.find(m => (m.userId || m.user?._id) === userProfile?._id)
        : null;
      setMyMembershipStatus(mine?.status || null);
      setMyCanApply(mine?.canApplyDespiteRestrictions || false);
    } catch (err) {
      console.error('Error refreshing membership state:', err);
    }
  };

  // Handle dropdown toggle
  const toggleDropdown = (e) => {
    e.stopPropagation();
    setShowDropdown(!showDropdown);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setShowDropdown(false);
    if (showDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showDropdown]);

  // Get registration dates info - must be before early return
  const registrationDates = useMemo(() => program ? getRegistrationDates(program) : null, [program]);

  if (!program) return null;

  const SportIcon = getSportIcon(program.sport);
  const price = formatProgramPrice(program);
  
  // Check if user is enrolled (don't show registration status for enrolled users)
  const isEnrolled = myMembershipStatus?.toLowerCase() === 'enrolled';
  
  // Format program dates for display
  const formatDateRange = (startDate, endDate) => {
    if (!startDate && !endDate) return null;
    
    const formatDate = (date) => {
      if (!date) return 'TBD';
      const d = new Date(date);
      const month = d.toLocaleDateString('en-US', { month: 'short' });
      const day = d.getDate();
      const year = d.getFullYear();
      const currentYear = new Date().getFullYear();
      // Only show year if different from current year
      return currentYear === year ? `${month} ${day}` : `${month} ${day}, ${year}`;
    };
    
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };
  
  const programDateRange = formatDateRange(program.schedule?.startDate, program.schedule?.endDate);
  const hasProgramDates = program.schedule?.startDate || program.schedule?.endDate;

  return (
    <div 
      className={`${styles.card} ${showDropdown ? styles.menuOpen : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
    >
      {/* Card Header */}
      <div className={styles.cardHeader}>
        <div className={styles.programInfo}>
          <SportIcon className={styles.sportIcon} />
          <h3 className={styles.programName}>{program.name}</h3>
        </div>

        <div className={styles.headerActions}>
          {/* Follow Button */}
          <div onClick={(e) => e.stopPropagation()}>
            <FollowButton
              programId={program._id}
              programName={program.name}
              className={styles.followButton}
            />
          </div>

          {/* Manager Dropdown */}
          {canManage && (
            <div className={styles.dropdownContainer}>
              <button 
                className={styles.menuButton}
                onClick={toggleDropdown}
              >
                <FaEllipsisV />
              </button>
              
              {showDropdown && (
                <div className={styles.dropdownMenu}>
                  <button onClick={(e) => { e.stopPropagation(); onEdit?.(program); setShowDropdown(false); }}>
                    <FaEdit /> Edit
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); onManage?.(program); setShowDropdown(false); }}>
                    <FaCog /> Manage
                  </button>
                  <button 
                    className={styles.deleteButton}
                    onClick={(e) => { e.stopPropagation(); onDelete?.(program._id); setShowDropdown(false); }}
                  >
                    <FaTrash /> Delete
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Quick Info */}
      <div className={styles.quickInfo}>
        {program.gameFormat && (
          <span className={styles.infoTag}>{program.gameFormat}</span>
        )}
        {program.age?.min && program.age?.max && (
          <span className={styles.infoTag}>Ages {program.age.min}-{program.age.max}</span>
        )}
        {program.gender && (
          <span className={styles.infoTag}>{program.gender}</span>
        )}
        <span className={styles.priceTag}>{price}</span>
      </div>

      {/* Capacity Indicator */}
      <div className={styles.capacitySection}>
        <CapacityIndicator
          program={program}
          showDetails={false}
          size="small"
        />
      </div>

      {/* Status Badges */}
      <div className={styles.statusSection}>
        {/* User's membership status */}
        {!membershipLoading && myMembershipStatus && !(myMembershipStatus === 'Waitlisted' && myCanApply) && (
          <MembershipStatusBadge status={myMembershipStatus} />
        )}
        
        {/* Following badge */}
        {!membershipLoading && followingStatus.isFollowing && !myMembershipStatus && (
          <MembershipStatusBadge 
            type="following" 
            isFollowing={followingStatus.isFollowing}
          />
        )}

        {/* Club role badge for managers */}
        {isClubManager && userClubRole && (
          <RoleBadge role={userClubRole} />
        )}
      </div>

      {/* Program Dates & Registration Status */}
      {(hasProgramDates || (registrationDates?.hasRegistrationDates && !isEnrolled)) && (
        <div className={styles.datesSection}>
          {/* Program Dates */}
          {hasProgramDates && (
            <div className={styles.programDates}>
              <FaCalendarAlt className={styles.dateIcon} />
              <span>{programDateRange}</span>
            </div>
          )}
          
          {/* Registration Status - only if not enrolled */}
          {registrationDates?.hasRegistrationDates && !isEnrolled && (
            <div className={`${styles.registrationStatus} ${styles[registrationDates.statusType]}`}>
              <span className={styles.regStatusIcon}>
                {registrationDates.statusType === 'open' && '●'}
                {registrationDates.statusType === 'upcoming' && '◐'}
                {registrationDates.statusType === 'closed' && '○'}
              </span>
              <span>{registrationDates.statusMessage}</span>
            </div>
          )}
        </div>
      )}

      {/* Action Button */}
      <div className={styles.actionSection}>
        <SmartActionButton
          program={program}
          clubId={clubId}
          myMembershipStatus={myMembershipStatus}
          isClubManager={isClubManager}
          enrolledCount={enrolledCount}
          onJoinClubRequired={onJoinClubRequired}
          onRegistrationComplete={onRegistrationComplete}
          onMembershipStatusChange={setMyMembershipStatus}
          refreshMembershipState={refreshMembershipState}
          size="small"
        />
      </div>
    </div>
  );
};

export default ProgramCard;










